import { AxiosError } from 'axios';

import { apiService } from '../../../shared/services/apiServices';
import { EMPLOYEE_ERROR_MESSAGES } from '../errors';
import employeeEndpoints from './employeeEndpoints';
import type { FacilityUserListItem } from './listFacilityUserApiService';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface UpdateEmployeePayload {
  name?: string;
  phoneNumber?: string;
  countryCode?: string;
  gender?: 'male' | 'female';
  academicTitle?: string;
}

export interface UpdateEmployeeResult {
  success: boolean;
  employee?: FacilityUserListItem;
  error?: string;
  accessDenied?: boolean;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const extractErrorMessage = (errorData: unknown): string => {
  if (!errorData) {
    return EMPLOYEE_ERROR_MESSAGES.UNEXPECTED_ERROR;
  }
  if (typeof errorData === 'string') {
    return errorData;
  }
  if (typeof errorData === 'object') {
    const apiError = errorData as { message?: string; error?: string };
    const msg = apiError.message || apiError.error;
    if (msg === 'Internal Server Error') {
      return EMPLOYEE_ERROR_MESSAGES.UNEXPECTED_ERROR;
    }
    if (msg?.startsWith('Access denied')) {
      return EMPLOYEE_ERROR_MESSAGES.ACCESS_DENIED;
    }
    return msg || EMPLOYEE_ERROR_MESSAGES.UNEXPECTED_ERROR;
  }
  return EMPLOYEE_ERROR_MESSAGES.UNEXPECTED_ERROR;
};

// ── updateEmployee ────────────────────────────────────────────────────────────

export const updateEmployee = async (
  employeeId: string,
  payload: UpdateEmployeePayload
): Promise<UpdateEmployeeResult> => {
  try {
    const result = await apiService.patchAPI(
      employeeEndpoints.EMPLOYEE_UPDATE(employeeId),
      payload
    );

    if (result.error) {
      if (result.status === 403) {
        return {
          success: false,
          accessDenied: true,
          error: EMPLOYEE_ERROR_MESSAGES.ACCESS_DENIED,
        };
      }
      return {
        success: false,
        error: result.isNetworkError
          ? EMPLOYEE_ERROR_MESSAGES.NETWORK_ERROR
          : extractErrorMessage(result.data),
      };
    }

    const data = result.data as {
      employee: {
        id: string;
        user_id: string | null;
        name: string;
        email: string;
        phone_number?: string | null;
        country_code?: string | null;
        gender?: string | null;
        academic_title?: string | null;
        facility_id: string;
        status: 'active' | 'inactive';
      };
    };

    return {
      success: true,
      employee: {
        ...data.employee,
        countryCode: data.employee.country_code ?? null,
        phone_number: data.employee.phone_number ?? null,
        gender: data.employee.gender ?? null,
        academic_title: data.employee.academic_title ?? null,
      },
    };
  } catch (error) {
    if (error instanceof AxiosError) {
      if (error.response?.status === 403) {
        return {
          success: false,
          accessDenied: true,
          error: EMPLOYEE_ERROR_MESSAGES.ACCESS_DENIED,
        };
      }
      const isNetworkError = !error.response && !!error.request;
      return {
        success: false,
        error: isNetworkError
          ? EMPLOYEE_ERROR_MESSAGES.NETWORK_ERROR
          : extractErrorMessage(error.response?.data),
      };
    }
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : EMPLOYEE_ERROR_MESSAGES.UNEXPECTED_ERROR,
    };
  }
};
